import type { ColumnDef, OnChangeFn, PaginationState, RowSelectionState } from "@tanstack/react-table";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { ButtonWithIcon } from "@/components/ui/button-with-icon";
import { DataTable } from "@/components/ui/data-table";
import { createDataTableSelectColumn } from "@/components/ui/data-table-select-column";
import { TableOutlineDeleteIconButton } from "@/components/ui/table-outline-delete-button";
import { EyeIcon, type EyeIconHandle } from "@/components/ui/eye";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ArchiveIcon, type ArchiveIconHandle } from "@/components/ui/archive-icon";
import { UndoIcon, type UndoIconHandle } from "@/components/ui/undo-icon";
import type { ReportStrategyPolicyVersion } from "../../../types/reports";
import { PolicyKindChips } from "@/components/Policy/PolicyKindChips";
import { resolvePolicyKindsFromVersion } from "@/utils/policyKindChips";
import {
  resolvePolicyVersionReportLinkState,
} from "./policyVersionReportLinks";
import {
  diffRuleKeys,
  extractPolicyRuleKeys,
  fmtUtcLike,
  policyVersionDisplayTitle,
  resolvePolicyVersionSimulationLabel,
  resolvePolicyVersionSourceDisplay,
  versionLabel,
} from "./strategyDetailUtils";

const POLICY_VERSIONS_PAGE_SIZE = 10;
/** Policy diff column — hidden until diff UX is ready. */
const SHOW_POLICY_VERSION_CHANGES_COLUMN = false;

function ActionCell({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div
      className="flex items-center gap-2"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

function PolicyVersionPlaceholder({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <span
      data-strategies-table-muted=""
      className="font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white"
    >
      {children}
    </span>
  );
}

function policyVersionRuleDelta(
  v: ReportStrategyPolicyVersion,
  versionsByNumber: Map<number, ReportStrategyPolicyVersion>
): { added: string[]; removed: string[] } | null {
  const prev =
    typeof v.version_number === "number" ? versionsByNumber.get(v.version_number - 1) : undefined;
  if (!prev) return null;
  const prevKeys = prev.policy_tree ? extractPolicyRuleKeys(prev.policy_tree) : [];
  const nextKeys = v.policy_tree ? extractPolicyRuleKeys(v.policy_tree) : [];
  return diffRuleKeys(prevKeys, nextKeys);
}

function PolicyVersionChangesCell({
  version,
  versionsByNumber,
}: {
  version: ReportStrategyPolicyVersion;
  versionsByNumber: Map<number, ReportStrategyPolicyVersion>;
}): React.ReactElement {
  const delta = policyVersionRuleDelta(version, versionsByNumber);
  if (!delta) {
    return <PolicyVersionPlaceholder>—</PolicyVersionPlaceholder>;
  }

  const { added, removed } = delta;
  if (added.length === 0 && removed.length === 0) {
    return <PolicyVersionPlaceholder>—</PolicyVersionPlaceholder>;
  }

  const maxRulesInTooltip = 8;
  const addedPreview = added.slice(0, maxRulesInTooltip);
  const removedPreview = removed.slice(0, maxRulesInTooltip);
  const addedOverflow = added.length - addedPreview.length;
  const removedOverflow = removed.length - removedPreview.length;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex min-w-[56px] cursor-help items-center gap-2 font-mono text-sm font-medium tabular-nums"
            aria-label={`Policy changes: ${added.length} added, ${removed.length} removed`}
          >
            {added.length > 0 ? (
              <span className="text-green-600 dark:text-green-500">+{added.length}</span>
            ) : null}
            {removed.length > 0 ? (
              <span className="text-[#E00101] dark:text-red-400">-{removed.length}</span>
            ) : null}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          sideOffset={4}
          className="flex max-w-[320px] flex-col items-start gap-2 font-[family-name:var(--font-primary)] text-xs leading-relaxed"
        >
          <div className="flex w-full flex-col gap-2">
            <p className="text-background/90">Compared to the previous version number.</p>
            {added.length > 0 ? (
              <div>
                <p className="font-medium text-green-300">Added rules ({added.length})</p>
                <ul className="mt-1 list-inside list-disc space-y-0.5 text-background/95">
                  {addedPreview.map((rule, index) => (
                    <li key={`add-${index}-${rule}`} className="break-all">
                      {rule}
                    </li>
                  ))}
                </ul>
                {addedOverflow > 0 ? (
                  <p className="mt-1 text-background/80">+{addedOverflow} more</p>
                ) : null}
              </div>
            ) : null}
            {removed.length > 0 ? (
              <div>
                <p className="font-medium text-red-300">Removed rules ({removed.length})</p>
                <ul className="mt-1 list-inside list-disc space-y-0.5 text-background/95">
                  {removedPreview.map((rule, index) => (
                    <li key={`rem-${index}-${rule}`} className="break-all">
                      {rule}
                    </li>
                  ))}
                </ul>
                {removedOverflow > 0 ? (
                  <p className="mt-1 text-background/80">+{removedOverflow} more</p>
                ) : null}
              </div>
            ) : null}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function PolicyVersionSimulationLabelCell({
  label,
}: {
  label: { text: string; title?: string };
}): React.ReactElement {
  const fullText = label.title ?? label.text;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <p
            data-policy-versions-simulation-label=""
            data-strategies-table-muted=""
            className="max-w-[180px] cursor-default truncate font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white"
          >
            {label.text}
          </p>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          sideOffset={4}
          className="max-w-[min(24rem,90vw)] font-[family-name:var(--font-primary)] text-xs leading-relaxed"
        >
          {fullText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ViewOutlineButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}): React.ReactElement {
  const iconRef = useRef<EyeIconHandle | null>(null);

  const startIconAnimation = useCallback(() => {
    void iconRef.current?.startAnimation();
  }, []);

  const stopIconAnimation = useCallback(() => {
    void iconRef.current?.stopAnimation();
  }, []);

  const iconHoverHandlers = useMemo(
    () =>
      disabled
        ? {}
        : {
            onPointerDownCapture: startIconAnimation,
            onMouseEnter: startIconAnimation,
            onMouseLeave: stopIconAnimation,
          },
    [disabled, startIconAnimation, stopIconAnimation]
  );

  return (
    <Button
      type="button"
      variant="outline-brand"
      size="sm"
      data-font="primary"
      className="h-8 shrink-0 px-3"
      disabled={disabled}
      onClick={onClick}
      {...iconHoverHandlers}
    >
      <EyeIcon
        ref={iconRef}
        data-icon="inline-start"
        size={16}
        className="shrink-0 text-current"
        aria-hidden
      />
      <span className="normal-case tracking-normal">{label}</span>
    </Button>
  );
}

function RestoreOutlineButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}): React.ReactElement {
  const iconRef = useRef<UndoIconHandle | null>(null);

  const startIconAnimation = useCallback(() => {
    void iconRef.current?.startAnimation();
  }, []);

  const stopIconAnimation = useCallback(() => {
    void iconRef.current?.stopAnimation();
  }, []);

  const iconHoverHandlers = useMemo(
    () =>
      disabled
        ? {}
        : {
            onPointerDownCapture: startIconAnimation,
            onMouseEnter: startIconAnimation,
            onMouseLeave: stopIconAnimation,
          },
    [disabled, startIconAnimation, stopIconAnimation]
  );

  return (
    <ButtonWithIcon
      type="button"
      variant="outline-orange"
      disabled={disabled}
      onClick={onClick}
      label={label}
      icon={
        <UndoIcon
          ref={iconRef}
          data-icon="inline-start"
          size={16}
          className="inline-flex shrink-0 items-center text-current"
          aria-hidden
        />
      }
      {...iconHoverHandlers}
    />
  );
}

function ArchiveOutlineButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}): React.ReactElement {
  const iconRef = useRef<ArchiveIconHandle | null>(null);

  const startIconAnimation = useCallback(() => {
    void iconRef.current?.startAnimation();
  }, []);

  const stopIconAnimation = useCallback(() => {
    void iconRef.current?.stopAnimation();
  }, []);

  const iconHoverHandlers = useMemo(
    () =>
      disabled
        ? {}
        : {
            onPointerDownCapture: startIconAnimation,
            onMouseEnter: startIconAnimation,
            onMouseLeave: stopIconAnimation,
          },
    [disabled, startIconAnimation, stopIconAnimation]
  );

  return (
    <ButtonWithIcon
      type="button"
      variant="outline-orange"
      disabled={disabled}
      onClick={onClick}
      label={label}
      icon={
        <ArchiveIcon
          ref={iconRef}
          data-icon="inline-start"
          size={16}
          className="inline-flex shrink-0 items-center text-current"
          aria-hidden
        />
      }
      {...iconHoverHandlers}
    />
  );
}

export type StrategyPolicyVersionsTableProps = {
  versions: ReportStrategyPolicyVersion[];
  versionLabels: Record<string, string>;
  activeComposedPolicyVersionIds: string[];
  emptyMessage?: string;
  onViewPolicy: (version: ReportStrategyPolicyVersion) => void;
  onViewPine: (version: ReportStrategyPolicyVersion) => void;
  onOpenBacktest: (backtestId: string) => void;
  onOpenReportFromBacktest: (backtestId: string) => void;
  reportIdByBacktestId: Map<string, string | null>;
  reportLinksLoading?: boolean;
  onArchiveVersion: (version: ReportStrategyPolicyVersion, archive: boolean) => void;
  onSetActiveComposedPine: (version: ReportStrategyPolicyVersion) => void;
  onDeleteVersion: (policyVersionId: string) => void;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
};

export function StrategyPolicyVersionsTable(props: StrategyPolicyVersionsTableProps): React.ReactElement {
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});
  const rowSelection = props.rowSelection ?? internalRowSelection;
  const onRowSelectionChange = props.onRowSelectionChange ?? setInternalRowSelection;
  const [selectAllFromHeader, setSelectAllFromHeader] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: POLICY_VERSIONS_PAGE_SIZE,
  });

  const pageCount = useMemo(() => {
    if (props.versions.length === 0) return 0;
    return Math.ceil(props.versions.length / pagination.pageSize);
  }, [pagination.pageSize, props.versions.length]);

  useEffect(() => {
    onRowSelectionChange({});
    setSelectAllFromHeader(false);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [props.versions, onRowSelectionChange]);

  useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      setSelectAllFromHeader(false);
    }
  }, [rowSelection]);

  useEffect(() => {
    setSelectAllFromHeader(false);
  }, [pagination.pageIndex]);

  useEffect(() => {
    if (pageCount > 0 && pagination.pageIndex >= pageCount) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [pageCount, pagination.pageIndex]);

  const versionsByNumber = useMemo(() => {
    const m = new Map<number, ReportStrategyPolicyVersion>();
    for (const v of props.versions) {
      if (typeof v.version_number === "number" && Number.isFinite(v.version_number)) {
        m.set(v.version_number, v);
      }
    }
    return m;
  }, [props.versions]);

  const activeRowId = useMemo(() => {
    return props.activeComposedPolicyVersionIds[0];
  }, [props.activeComposedPolicyVersionIds]);

  const columns = useMemo((): ColumnDef<ReportStrategyPolicyVersion>[] => {
    const {
      versionLabels,
      activeComposedPolicyVersionIds,
      onViewPolicy,
      onViewPine,
      onOpenBacktest,
      onOpenReportFromBacktest,
      reportIdByBacktestId,
      reportLinksLoading,
      onArchiveVersion,
      onSetActiveComposedPine,
      onDeleteVersion,
    } = props;

    return [
      createDataTableSelectColumn<ReportStrategyPolicyVersion>({
        selectAllFromHeader,
        onSelectAllFromHeaderChange: setSelectAllFromHeader,
        headerAriaLabel: "Select all policy versions",
        getRowAriaLabel: (row) => `Select ${versionLabel(row.original)}`,
        headerDataAttributes: {
          "data-trade-log-select-all-header": "",
        },
      }),
      {
        id: "version",
        accessorFn: (row) => policyVersionDisplayTitle(row),
        header: "Version",
        cell: ({ row }) => {
          const v = row.original;
          const kinds = resolvePolicyKindsFromVersion(v);
          const name = policyVersionDisplayTitle(v);
          const short = versionLabel(v);

          return (
            <div data-strategies-table-name="" className="flex min-w-[140px] flex-col gap-1">
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                <span
                  className="truncate font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500 dark:text-white"
                  title={name}
                >
                  {name}
                </span>
                <PolicyKindChips kinds={kinds} />
              </div>
              {name !== short ? (
                <span className="font-[family-name:var(--font-primary)] text-xs font-normal text-muted-foreground">
                  {short}
                </span>
              ) : null}
            </div>
          );
        },
        enableSorting: false,
      },
      ...(SHOW_POLICY_VERSION_CHANGES_COLUMN
        ? [
            {
              id: "changes",
              header: "Changes",
              cell: ({ row }: { row: { original: ReportStrategyPolicyVersion } }) => (
                <PolicyVersionChangesCell
                  version={row.original}
                  versionsByNumber={versionsByNumber}
                />
              ),
              enableSorting: false,
            } satisfies ColumnDef<ReportStrategyPolicyVersion>,
          ]
        : []),
      {
        id: "simulationLabel",
        accessorFn: (row) =>
          resolvePolicyVersionSimulationLabel(row, props.versions)?.text ?? "",
        header: "Simulation Label",
        cell: ({ row }) => {
          const label = resolvePolicyVersionSimulationLabel(row.original, props.versions);
          if (!label) {
            return <PolicyVersionPlaceholder>—</PolicyVersionPlaceholder>;
          }
          return <PolicyVersionSimulationLabelCell label={label} />;
        },
        enableSorting: false,
      },
      {
        id: "created",
        accessorFn: (row) => row.created_at ?? "",
        header: "Created",
        cell: ({ row }) => (
          <p
            data-strategies-table-muted=""
            className="whitespace-nowrap font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white"
          >
            {fmtUtcLike(row.original.created_at)}
          </p>
        ),
        enableSorting: false,
      },
      {
        id: "source",
        accessorFn: (row) =>
          resolvePolicyVersionSourceDisplay(row, props.versions)?.text ??
          row.source_backtest_title ??
          row.source_backtest_id ??
          "",
        header: "Source",
        cell: ({ row }) => {
          const source = resolvePolicyVersionSourceDisplay(row.original, props.versions);
          if (!source) {
            return <PolicyVersionPlaceholder>—</PolicyVersionPlaceholder>;
          }
          return (
            <p
              data-policy-versions-source=""
              data-strategies-table-muted=""
              className="max-w-[180px] truncate font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white"
              title={source.title}
            >
              {source.text}
            </p>
          );
        },
        enableSorting: false,
      },
      {
        id: "viewPolicy",
        header: "Policy",
        cell: ({ row }) => {
          const v = row.original;
          return (
            <ActionCell>
              <ViewOutlineButton
                label="View"
                onClick={() => onViewPolicy(v)}
                disabled={!v.policy_tree}
              />
            </ActionCell>
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: "viewPine",
        header: "Pinescript",
        cell: ({ row }) => (
          <ActionCell>
            <ViewOutlineButton label="View" onClick={() => onViewPine(row.original)} />
          </ActionCell>
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: "simulation",
        header: "Simulation",
        cell: ({ row }) => {
          const backtestId = row.original.source_backtest_id;
          if (!backtestId) {
            return (
              <ActionCell>
                <PolicyVersionPlaceholder>N/A</PolicyVersionPlaceholder>
              </ActionCell>
            );
          }
          return (
            <ActionCell>
              <Button
                type="button"
                variant="outline-gray"
                size="sm"
                data-font="primary"
                className="h-8 shrink-0 px-3"
                onClick={() => onOpenBacktest(String(backtestId))}
              >
                <span className="normal-case tracking-normal">Open</span>
              </Button>
            </ActionCell>
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: "report",
        header: "Report",
        cell: ({ row }) => {
          const backtestId = row.original.source_backtest_id;
          const reportLinkState = resolvePolicyVersionReportLinkState(
            backtestId,
            reportIdByBacktestId,
            Boolean(reportLinksLoading)
          );

          if (reportLinkState === "no-simulation" || reportLinkState === "no-report") {
            return (
              <ActionCell>
                <PolicyVersionPlaceholder>N/A</PolicyVersionPlaceholder>
              </ActionCell>
            );
          }

          if (reportLinkState === "pending") {
            return (
              <ActionCell>
                <PolicyVersionPlaceholder>—</PolicyVersionPlaceholder>
              </ActionCell>
            );
          }

          return (
            <ActionCell>
              <Button
                type="button"
                variant="outline-gray"
                size="sm"
                data-font="primary"
                className="h-8 shrink-0 px-3"
                onClick={() => void onOpenReportFromBacktest(String(backtestId))}
              >
                <span className="normal-case tracking-normal">Open</span>
              </Button>
            </ActionCell>
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: "current",
        header: "Current",
        cell: ({ row }) => {
          const v = row.original;
          const isActiveComposed = activeComposedPolicyVersionIds.includes(v.id);
          return (
            <ActionCell>
              {isActiveComposed ? (
                <Button
                  type="button"
                  variant="gradient-green"
                  size="sm"
                  data-font="primary"
                  className="h-8 shrink-0 px-3 disabled:!opacity-100"
                  disabled
                >
                  <span className="normal-case tracking-normal">Current</span>
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="gradient"
                  size="sm"
                  data-font="primary"
                  className="h-8 shrink-0 px-3"
                  disabled={Boolean(v.is_archived)}
                  onClick={() => onSetActiveComposedPine(v)}
                >
                  <span className="normal-case tracking-normal">Set</span>
                </Button>
              )}
            </ActionCell>
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: "actions",
        header: "Actions",
        size: 140,
        cell: ({ row }) => {
          const v = row.original;
          return (
            <ActionCell>
              {v.is_archived ? (
                <RestoreOutlineButton
                  label="Restore"
                  onClick={() => void onArchiveVersion(v, false)}
                />
              ) : (
                <ArchiveOutlineButton
                  label="Archive"
                  onClick={() => void onArchiveVersion(v, true)}
                />
              )}
              <TableOutlineDeleteIconButton
                marker="strategies"
                aria-label={`Delete ${versionLabel(v)}`}
                onClick={() => void onDeleteVersion(v.id)}
              />
            </ActionCell>
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
    ];
  }, [props, selectAllFromHeader, versionsByNumber]);

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    onRowSelectionChange((prev) => (typeof updater === "function" ? updater(prev) : updater));
  };

  const showPagination = props.versions.length > 0;

  return (
    <div
      data-policy-versions-table=""
      data-trade-log-trades-table=""
      className="flex w-full min-w-0 flex-col gap-4"
    >
      <DataTable
        columns={columns}
        data={props.versions}
        getRowId={(row) => row.id}
        activeRowId={activeRowId}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
        pagination={pagination}
        onPaginationChange={setPagination}
        showPagination={showPagination}
        paginationAfterShell={showPagination}
        emptyMessage={props.emptyMessage ?? "No policy versions."}
        className="min-w-0 w-full"
        tableClassName="dark:!bg-sidebar"
      />
    </div>
  );
}
